import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { Chatv1Error } from '../chatv1/transport'
import { onChatv1Frame, onChatv1Open, type Frame } from '../chatv1/ws'
import { acked, coalesce, emptyLive, forget, fromRead, fromServer, local, pendingOps, view, type LiveOp, type LiveRow, type LiveState } from './boardState'
import { newId } from './model'

/**
 * ROWS THE ROOM WRITES TOGETHER, LIVE — a board's elements (useBoard), a page's blocks (page/usePage): read when the
 * screen opens and every time the socket comes back (a frame missed while the socket was down never arrives late; the
 * chat contract's reconcile-on-open), changed by frames as anyone writes, and written by this person through ONE
 * request at a time, in order, so the service's last-writer-wins sees this person's changes in the order they were made.
 *
 * A drag sends while it moves (`live`: at most every SEND_EVERY ms) so the others watch the shape travel; the
 * gesture is ONE undo step, recorded against the rows as they were when the gesture began (where the wire can undo).
 *
 * One board (one page) at a time. Picking another — or closing it — a moment after a change still delivers that
 * change: what this screen has not heard confirmed goes once more, after the request in flight, to the one it was
 * made on.
 */
const SEND_EVERY = 120
const MAX_OPS = 500
const RETRY_MAX = 15_000

/** How one kind of rows reaches the service: its read, its write, its frames, and what its refusals say. */
export type LiveWire<E extends LiveRow, P extends object> = {
  read: (conv: string, doc: string) => Promise<{ rev?: number; rows?: E[] }>
  post: (conv: string, doc: string, ops: Array<LiveOp<P>>, client: string) => Promise<{ rev?: number; rows?: E[]; refused?: Array<{ id: string; error: string }> }>
  /** A frame about this one (board, page): its rows, or `reload` — null when it is about something else. */
  frame: (f: Frame, conv: string, doc: string) => { rows?: E[]; rev?: number; reload?: boolean; by: string; via?: string; client?: string } | null
  /** The refusal that means "not in this room" — the screen goes back. */
  missing: string
  refusal: (code: string) => string
  /** The writes that undo these — absent: the screen keeps its own undo (a page's editor). */
  inverse?: (before: Record<string, E>, ops: Array<LiveOp<P>>) => Array<LiveOp<P>>
}

export type ApplyOpts<E extends LiveRow = LiveRow> = {
  /** 'none': no undo step (a drag in progress, an undo itself). */
  history?: 'push' | 'none'
  /** The rows as they were before the gesture (for a gesture's single undo step). */
  before?: Record<string, E>
  /** Part of a drag: send soon, not at once. */
  live?: boolean
}

type Step<P extends object> = { undo: Array<LiveOp<P>>; redo: Array<LiveOp<P>> }

/** Someone ELSE changed it just now: who (a person, or a person's agent) and what. */
export type Activity = { by: string; via?: string; added: number; removed: number; changed: number }

/** What a frame did, counted against the rows as this screen held them before. */
export function activityOf(before: Record<string, LiveRow>, f: { by: string; via?: string | undefined; elements: LiveRow[] }): Activity {
  let added = 0
  let removed = 0
  let changed = 0
  for (const el of f.elements) {
    const had = before[el.id]
    if (el.del) {
      if (had) removed++
    } else if (!had) added++
    else changed++
  }
  return { by: f.by, ...(f.via ? { via: f.via } : {}), added, removed, changed }
}

export function useLiveRows<E extends LiveRow, P extends object>(wire: LiveWire<E, P>, convId: string | null, boardId: string) {
  const st = useRef<LiveState<E, P>>(emptyLive<E, P>())
  const [tick, bump] = useReducer((n: number) => n + 1, 0)
  const set = useCallback((fn: (s: LiveState<E, P>) => LiveState<E, P>) => {
    const next = fn(st.current)
    if (next !== st.current) {
      st.current = next
      bump()
    }
  }, [])
  /** missing: no such board in this room (a stale link) — the screen goes back to the room's first. */
  const [status, setStatus] = useState<'loading' | 'ready' | 'error' | 'missing'>('loading')
  const [saving, setSaving] = useState<'saved' | 'saving' | 'offline'>('saved')
  const [error, setError] = useState<string | null>(null)
  const [activity, setActivity] = useState<Activity | null>(null)

  const seq = useRef(0)
  const queue = useRef<Array<{ ops: Array<LiveOp<P>>; seq: number }>>([])
  const inflight = useRef(false)
  /** The request in flight, so a last send after it lands in order. */
  const inflightP = useRef<Promise<unknown> | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const backoff = useRef(1000)
  const client = useRef(newId())
  const epoch = useRef(0)
  const undoStack = useRef<Array<Step<P>>>([])
  const redoStack = useRef<Array<Step<P>>>([])
  // The room and board the queue belongs to: changed only where the queue is emptied (the effect below), never at
  // render — a drag's timer firing in between would otherwise send one board's moves to the next.
  const conv = useRef(convId)
  const which = useRef(boardId)

  // On the render where another board is picked, the state still holds the board before it (it is emptied in the
  // effect below): that render shows nothing and says `loading`, so nothing — a fit, a selection — acts on the old
  // board's picture as if it were the new one's.
  const mine = which.current === boardId
  const els = useMemo(() => (mine ? view(st.current) : ({} as Record<string, E>)), [tick, mine]) // eslint-disable-line react-hooks/exhaustive-deps
  const elsRef = useRef(els)
  elsRef.current = els

  const read = useCallback(async () => {
    const id = conv.current
    if (!id) return
    const mine = epoch.current
    try {
      const r = await wire.read(id, which.current)
      if (epoch.current !== mine) return
      set((s) => fromRead(s, r.rows ?? [], r.rev ?? 0))
      setStatus('ready')
    } catch (e) {
      if (epoch.current !== mine) return
      if (e instanceof Chatv1Error && e.code === wire.missing) {
        setStatus('missing')
        return
      }
      setStatus((cur) => (cur === 'ready' ? cur : 'error'))
      if (e instanceof Chatv1Error && e.code === 'no_board') setError(wire.refusal('no_board'))
    }
  }, [set, wire])

  // Another room, or another of its boards: nothing carries over.
  useEffect(() => {
    conv.current = convId
    which.current = boardId
    epoch.current++
    st.current = emptyLive<E, P>()
    queue.current = []
    inflight.current = false
    undoStack.current = []
    redoStack.current = []
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    setStatus('loading')
    setSaving('saved')
    setError(null)
    setActivity(null)
    bump()
    if (!convId) return
    void read()
    const offFrame = onChatv1Frame((frame) => {
      const f = wire.frame(frame, convId, boardId)
      if (!f) return
      if (f.reload || !f.rows) {
        void read()
        return
      }
      // Another screen's change (another person, their agent, or this person's other device): said, briefly.
      if (f.client !== client.current) {
        const a = activityOf(elsRef.current, { by: f.by, via: f.via, elements: f.rows })
        if (a.added + a.removed + a.changed) setActivity(a)
      }
      set((s) => fromServer(s, f.rows!, f.rev))
    })
    const offOpen = onChatv1Open(() => void read())
    // Answers still on their way belong to this board: leaving it makes them stale (the ref objects, held here).
    const ep = epoch
    const state = st
    const q = queue
    const t = timer
    const inP = inflightP
    const me = client.current
    return () => {
      offFrame()
      offOpen()
      ep.current++
      // A change made a moment ago (a drag's last move still on its timer, or queued behind a request) is still
      // this person's: it goes, once more and in full, after whatever is in flight.
      if (t.current) clearTimeout(t.current)
      t.current = null
      q.current = []
      const last = coalesce(pendingOps(state.current))
      if (last.length) {
        void (inP.current ?? Promise.resolve())
          .catch(() => undefined)
          .then(() => wire.post(convId, boardId, last, me))
          .catch(() => undefined)
      }
    }
  }, [convId, boardId, read, set, wire])

  const flush = useCallback(() => {
    timer.current = null
    const id = conv.current
    if (!id || inflight.current || !queue.current.length) return
    const batch: Array<{ ops: Array<LiveOp<P>>; seq: number }> = []
    let n = 0
    while (queue.current.length && n + queue.current[0]!.ops.length <= MAX_OPS) {
      const b = queue.current.shift()!
      batch.push(b)
      n += b.ops.length
    }
    if (!batch.length) batch.push(queue.current.shift()!) // one write larger than a request: sent alone
    const ops = coalesce(batch.flatMap((b) => b.ops))
    const last = batch[batch.length - 1]!.seq
    inflight.current = true
    setSaving('saving')
    const mine = epoch.current
    const req = wire.post(id, which.current, ops, client.current)
    inflightP.current = req
    req
      .then((r) => {
        if (epoch.current !== mine) return
        backoff.current = 1000
        const refused = r.refused ?? []
        set((s) => acked(forget(fromServer(s, r.rows ?? [], r.rev), refused.map((x) => x.id)), last))
        if (refused.length) setError(wire.refusal(refused[0]!.error))
        inflight.current = false
        if (queue.current.length) flush()
        else setSaving('saved')
      })
      .catch((e) => {
        if (epoch.current !== mine) return
        inflight.current = false
        const permanent = e instanceof Chatv1Error && e.status >= 400 && e.status < 500 && e.status !== 401 && e.status !== 429
        if (permanent) {
          // Refused as a whole: the service's picture stands; this person is told why.
          set((s) => acked(forget(s, ops.map((o) => o.id)), last))
          setError(wire.refusal((e as Chatv1Error).code))
          if (queue.current.length) flush()
          else setSaving('saved')
          return
        }
        // Not delivered: everything still pending goes again, as it stands now, once the way is back.
        queue.current = [{ ops: pendingOps(st.current), seq: seq.current }]
        setSaving('offline')
        const wait = backoff.current
        backoff.current = Math.min(RETRY_MAX, backoff.current * 2)
        timer.current = setTimeout(flush, wait)
      })
  }, [set, wire])

  // The socket came back: whatever waited for the way back goes now.
  useEffect(() => onChatv1Open(() => {
    if (queue.current.length && !inflight.current) {
      if (timer.current) clearTimeout(timer.current)
      flush()
    }
  }), [flush])

  const apply = useCallback(
    (ops: Array<LiveOp<P>>, opts: ApplyOpts<E> = {}) => {
      if (!ops.length || !conv.current) return
      setActivity(null)
      const before = opts.before ?? elsRef.current
      const n = ++seq.current
      set((s) => local(s, ops, n))
      queue.current.push({ ops, seq: n })
      if (opts.history !== 'none' && wire.inverse) {
        undoStack.current.push({ undo: wire.inverse(before, ops), redo: ops })
        if (undoStack.current.length > 200) undoStack.current.shift()
        redoStack.current = []
      }
      if (opts.live) {
        if (!timer.current && !inflight.current) timer.current = setTimeout(flush, SEND_EVERY)
      } else {
        if (timer.current) clearTimeout(timer.current)
        timer.current = null
        flush()
      }
    },
    [flush, set, wire],
  )

  /** A gesture that sent its moves `live` and ends now: one undo step from where it began. */
  const record = useCallback((before: Record<string, E>, ops: Array<LiveOp<P>>) => {
    if (!ops.length || !wire.inverse) return
    undoStack.current.push({ undo: wire.inverse(before, ops), redo: ops })
    redoStack.current = []
    bump()
  }, [wire])

  const undo = useCallback(() => {
    const step = undoStack.current.pop()
    if (!step) return
    redoStack.current.push(step)
    apply(step.undo, { history: 'none' })
  }, [apply])

  const redo = useCallback(() => {
    const step = redoStack.current.pop()
    if (!step) return
    undoStack.current.push(step)
    apply(step.redo, { history: 'none' })
  }, [apply])

  return {
    els,
    /** The newest write of this board this screen holds — what the person has in front of them (boardSeen.ts). */
    rev: mine ? st.current.rev : 0,
    status: mine ? status : ('loading' as const),
    saving,
    error,
    clearError: () => setError(null),
    /** Tell the person something about the board in the same place a refusal is told. */
    notify: (text: string) => setError(text),
    activity,
    clearActivity: () => setActivity(null),
    apply,
    record,
    undo,
    redo,
    canUndo: undoStack.current.length > 0,
    canRedo: redoStack.current.length > 0,
    reload: read,
  }
}


